import clsx from 'clsx'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import { ChangeEventHandler, FocusEventHandler, useState } from 'react'
import { Html5InputType } from 'types'
import InputLabel from './InputLabel'

type InputOption = {
  name: string
  value: string | number
  selected?: boolean
}

interface WifInputProps {
  label?: string
  type?: Html5InputType
  error: boolean | undefined
  errorMessage: string
  icon?: string
  name?: string
  value: any
  onChange: ChangeEventHandler<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  onBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  disabled?: boolean
  options?: InputOption[]
  customInputClass?: string
  customWrapperClass?: string
  customErrorClass?: string
  selectPlaceholder?: string
  placeholder?: string
  rows?: number
  cols?: number
  inputUnit?: string | null | undefined
}

/**
 * Custom input field created for Wif application. used in most form except Login / Register form.
 *
 * @param {Array<InputOption>} options used for generating list of input type='select'
 * @param {string} errorMessage expected to be a string, and preferably return of `t()` (useTranslation)
 * @param {string} inputUnit a string of unit for input type='text'. this will render on the right side of the input
 *
 * @returns
 */

export const WifInput = ({
  label,
  type = 'text',
  error,
  errorMessage,
  icon,
  name,
  value,
  onChange,
  onBlur,
  disabled = false,
  options,
  customInputClass,
  customWrapperClass,
  customErrorClass,
  selectPlaceholder,
  placeholder,
  rows,
  cols,
  inputUnit,
}: WifInputProps) => {
  const [isPassShown, setIsPassShown] = useState(false)
  const { t } = useTranslation()

  const togglePassReveal = () => {
    setIsPassShown(!isPassShown)
  }

  const renderInputType = (type: Html5InputType) => {
    if (type === 'password') {
      return isPassShown ? 'text' : 'password'
    }
    return type
  }

  return (
    <div className={clsx('relative', 'mb-4', icon && 'pl-14', customWrapperClass)}>
      {label && <InputLabel text={label} name={name} />}

      {icon && (
        <div className='icon-placeholder absolute top-1/2 -translate-y-1/2 leading-[0px] left-3'>
          <Image src={icon} alt={label + ' logo'} width={30} height={30} />
        </div>
      )}

      {type === 'password' && (
        <div
          className='input-icon absolute right-0 -top-2 cursor-pointer'
          onClick={togglePassReveal}
        >
          <Image
            src={isPassShown ? '/ic-pass-show.svg' : '/ic-pass-hide.svg'}
            alt='Password reveal'
            width={28}
            height={28}
          />
        </div>
      )}

      {type === 'select' && (
        <>
          <select
            className={clsx([
              'p-4',
              'w-full',
              'focus:outline-none',
              'border',
              'rounded-lg',
              'text-sm',
              'appearance-none',
              'bg-transparent',
              'block',
              'font-semibold',
              'cursor-pointer',
              error ? 'border-error-red' : 'border-medium-grey',
              disabled ? 'text-silver-gray bg-medium-grey' : 'text-black bg-white',
              customInputClass,
            ])}
            name={name}
            onChange={(e) => onChange(e)}
            onBlur={(e) => onBlur(e)}
            value={value}
            placeholder={placeholder}
            disabled={disabled}
          >
            {selectPlaceholder && (
              <option
                className={clsx(
                  disabled ? 'text-silver-gray bg-medium-grey' : 'text-black bg-white'
                )}
                value=''
                disabled
              >
                {selectPlaceholder}
              </option>
            )}
            {options?.map(({ value: optionValue, name }) => (
              <option className='text-[#303030]' key={name} value={optionValue}>
                {t(name)}
              </option>
            ))}
          </select>
          <div className='absolute top-[calc(50%+12px)] -translate-y-1/2 right-2 flex'>
            <Image src='/icons/ic-chevron-down.svg' alt={label + ' logo'} width={20} height={20} />
          </div>
        </>
      )}

      {(type === 'text' ||
        type === 'password' ||
        type === 'email' ||
        type === 'tel' ||
        type === 'number') && (
        <div className='flex items-stretch justify-start'>
          <input
            id={name}
            name={name}
            type={renderInputType(type)}
            className={clsx(
              'p-4',
              'border',
              'rounded-lg',
              'text-sm',
              'w-full',
              'font-semibold',
              'block',
              'focus:outline-none',
              'placeholder:text-[#BDBDBD]',
              'placeholder:font-medium',
              error ? 'border-error-red' : 'border-medium-grey',
              disabled ? 'text-silver-gray bg-medium-grey' : 'text-black bg-white',
              customInputClass
            )}
            value={type === 'number' ? (!value ? '' : Number(value).toString()) : value}
            placeholder={placeholder}
            autoComplete={label}
            onChange={onChange}
            onBlur={onBlur}
            onKeyDown={
              type === 'tel'
                ? (e) => {
                    const keyCode = e.keyCode
                    const excludedKeys = [8, 37, 39, 46, 187, 91]
                    if (
                      !(
                        (keyCode >= 48 && keyCode <= 57) ||
                        (keyCode >= 96 && keyCode <= 105) ||
                        excludedKeys.includes(keyCode)
                      )
                    ) {
                      e.preventDefault()
                    }
                  }
                : () => {}
            }
            onFocus={
              type === 'tel'
                ? (e) => {
                    if (!e.target.value) e.target.value = '+62'
                  }
                : () => {}
            }
            disabled={disabled}
          />

          {!!inputUnit && (
            <div
              className={clsx(
                'flex flex-shrink justify-center items-center min-w-[65px] text-sm font-semibold',
                'rounded-tr-lg rounded-br-lg border border-l-0',
                disabled ? 'text-silver-gray bg-medium-grey' : 'text-black bg-white',
                error ? 'border-error-red' : 'border-medium-grey'
              )}
            >
              {inputUnit}
            </div>
          )}
        </div>
      )}

      {type === 'textarea' && (
        <textarea
          disabled={disabled}
          id={name}
          name={name}
          rows={rows}
          cols={cols}
          placeholder={placeholder}
          className={clsx(
            'p-4',
            'border',
            'rounded-lg',
            'text-sm',
            'w-full',
            'font-semibold',
            'block',
            'focus:outline-none',
            'bg-transparent',
            'placeholder:text-[#BDBDBD]',
            'placeholder:font-medium',
            'resize-none',
            error ? 'border-error-red' : 'border-medium-grey',
            disabled ? 'text-silver-gray bg-medium-grey' : 'text-black bg-white',
            customInputClass
          )}
          onChange={onChange}
          onBlur={onBlur}
          value={value}
        />
      )}

      {error && (
        <p className={clsx('text-error-red text-xs font-normal', customErrorClass)}>
          {errorMessage}
        </p>
      )}
    </div>
  )
}
